import { Type } from 'class-transformer';
import {
  buildMessage,
  IsArray,
  IsBoolean,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  Min,
  ValidateBy,
  ValidateIf,
  ValidateNested,
  type ValidationArguments,
} from 'class-validator';
import { MAX_INLINE_COMPONENT_DEPTH } from '../../allergen-core/composition-limits';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';
import { ADDITIVE_FUNCTIONS, type AdditiveFunction } from './additive-function.enum';
import {
  DECLARED_PERCENT_MAX,
  DECLARED_PERCENT_MIN,
  E_NUMBER_PATTERN,
} from './ingredient-component-limits';

export { DECLARED_PERCENT_MAX, DECLARED_PERCENT_MIN, E_NUMBER_PATTERN };

function IsAbsentWhenLinked(): PropertyDecorator {
  return ValidateBy({
    name: 'isAbsentWhenLinked',
    validator: {
      validate: (value: unknown, validationArguments?: ValidationArguments) => {
        const component = validationArguments?.object as IngredientComponentDto | undefined;
        const isEmptyList = Array.isArray(value) && value.length === 0;
        return (
          !component?.linkedIngredientId || value === undefined || value === null || isEmptyList
        );
      },
      defaultMessage: buildMessage(
        (eachPrefix) =>
          `${eachPrefix}$property must be empty when the component is linked to an ingredient`,
      ),
    },
  });
}

/** Livelli inline sotto `value` (array di sotto-voci): 0 se vuoto. */
function inlineSubtreeHeight(value: unknown): number {
  if (!Array.isArray(value) || value.length === 0) {
    return 0;
  }
  const childHeights = value.map((child: unknown) =>
    typeof child === 'object' && child !== null && 'children' in child
      ? inlineSubtreeHeight(child.children)
      : 0,
  );
  return 1 + Math.max(...childHeights);
}

/**
 * Le sotto-voci di una riga scendono al massimo di `MAX_INLINE_COMPONENT_DEPTH`
 * livelli. Il vincolo si controlla a ogni livello sul proprio sottoalbero: la
 * riga di primo livello e' quella che lo fa scattare.
 */
export function MaxInlineComponentDepth(): PropertyDecorator {
  return ValidateBy({
    name: 'maxInlineComponentDepth',
    validator: {
      validate: (value: unknown) => inlineSubtreeHeight(value) <= MAX_INLINE_COMPONENT_DEPTH,
      defaultMessage: buildMessage(
        (eachPrefix) =>
          `${eachPrefix}$property must not nest more than ${MAX_INLINE_COMPONENT_DEPTH} levels of sub-items`,
      ),
    },
  });
}

/**
 * One row of an Ingredient's Annex VII Part E breakdown — the
 * "prosciutto contiene: carne di suino, sale, conservante: nitrito di
 * sodio (E250)" list. `isCarryOverExempt` marks a carry-over additive
 * (art. 20 lett. b), which the derived ingredient list omits from print.
 */
export class IngredientComponentDto {
  /**
   * Obbligatorio solo per una riga semplice. Su una riga linkata
   * (`linkedIngredientId`) il server scrive `labelName ?? name` del figlio:
   * il valore inviato dal client non viene mai letto.
   */
  @ValidateIf((component: IngredientComponentDto) => !component.linkedIngredientId)
  @IsString()
  @IsNotEmpty()
  name!: string;

  /**
   * Riga linkata a un ingrediente esistente: allergeni e additivi derivano dal
   * figlio, quindi i campi propri non sono ammessi insieme al link.
   */
  @IsOptional()
  @IsUUID()
  linkedIngredientId?: string;

  @IsInt()
  @Min(0)
  sortOrder!: number;

  @IsOptional()
  @IsUUID()
  @IsAbsentWhenLinked()
  allergenId?: string;

  /** Id opaco della riga di catalogo — stesso motivo di
   * `IngredientAllergenDto.substanceId`: non tutte le sostanze hanno un uuid
   * conforme e la foreign key basta a garantirne l'esistenza. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  @IsAbsentWhenLinked()
  substanceId?: string;

  @IsOptional()
  @IsIn(ADDITIVE_FUNCTIONS)
  @IsAbsentWhenLinked()
  additiveFunction?: AdditiveFunction;

  @IsOptional()
  @IsString()
  @Matches(E_NUMBER_PATTERN)
  @IsAbsentWhenLinked()
  additiveENumber?: string;

  @IsBoolean()
  isCarryOverExempt!: boolean;

  @IsIn(FIELD_VALUE_SOURCES)
  source!: FieldValueSource;

  /**
   * Percentuale come stampata sull'etichetta del fornitore, riferita alla voce
   * madre ("cacao 53%"). Ammessa anche su una riga collegata: e' una proprieta'
   * della riga nel padre. Solo testo dell'elenco: mai QUID ne' nutrizione.
   */
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2, allowNaN: false, allowInfinity: false })
  @Min(DECLARED_PERCENT_MIN)
  @Max(DECLARED_PERCENT_MAX)
  declaredPercent?: number;

  /**
   * Sotto-voci inline di una riga semplice (la voce composta dentro un
   * semilavorato, che il cliente non compra a parte). Una riga collegata ha
   * le voci del proprio ingrediente: niente figli inline.
   */
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientComponentDto)
  @MaxInlineComponentDepth()
  @IsAbsentWhenLinked()
  children?: IngredientComponentDto[];
}
